import axiosClient from "../axiosInstance";

const publicBase = "/public";
const adminBase = "/admin/organisations";

/**
 * Public endpoints. The slug is only needed when previewing a page on the main
 * domain at /rankings/:slug - on an organisation's own domain the server
 * resolves it from the request's host and the slug is ignored.
 */
export const getPublicOrganisation = async (slug) => {
  const response = await axiosClient.get(`${publicBase}/org`, {
    params: slug ? { org: slug } : undefined,
  });
  return response.data;
};

export const getPublicRankings = async ({ slug, date, gender } = {}) => {
  const params = {};
  if (slug) params.org = slug;
  if (date) params.date = date;
  if (gender) params.gender = gender;

  const response = await axiosClient.get(`${publicBase}/rankings`, { params });
  return response.data;
};

/**
 * Admin-only: the calculation path for one entry as displayed on this
 * organisation's page (effective allocations + year weight).
 */
export const getPublicEntryBreakdown = async (entryId, slug) => {
  const response = await axiosClient.get(
    `${publicBase}/entry-breakdown/${entryId}`,
    { params: slug ? { org: slug } : undefined },
  );
  return response.data;
};

/** Admin CRUD. Requires an admin Kinde token. */
export const listOrganisations = async () => {
  const response = await axiosClient.get(adminBase);
  return response.data;
};

export const createOrganisation = async (payload) => {
  const response = await axiosClient.post(adminBase, payload);
  return response.data;
};

export const updateOrganisation = async (id, payload) => {
  const response = await axiosClient.put(`${adminBase}/${id}`, payload);
  return response.data;
};

export const deleteOrganisation = async (id) => {
  const response = await axiosClient.delete(`${adminBase}/${id}`);
  return response.data;
};

/**
 * Upload a branding image and get back its public URL.
 *
 * Sent as a base64 data URL rather than multipart, matching how the CSV import
 * already moves file content through this API. The server decodes it and stores
 * it with the service role, so the browser never touches the storage bucket.
 */
export const uploadOrganisationAsset = async ({ file, slug, kind }) => {
  const response = await axiosClient.post(`${adminBase}/assets`, {
    file,
    slug,
    kind,
  });
  return response.data;
};

/** Read a File from an <input type="file"> into a base64 data URL. */
export const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () =>
      reject(reader.error || new Error("Could not read that file"));
    reader.readAsDataURL(file);
  });

const organisationService = {
  getPublicOrganisation,
  getPublicRankings,
  getPublicEntryBreakdown,
  listOrganisations,
  createOrganisation,
  updateOrganisation,
  deleteOrganisation,
};

export default organisationService;
